import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { qs } from '../lib/api.js';
import { useApi } from '../lib/hooks.js';
import { ErrorNote, Empty, Loading } from '../components/ui.jsx';
import OnHandPicker from '../components/OnHandPicker.jsx';
import SearchHit from '../components/SearchHit.jsx';

/**
 * Req #12: search the curated sources and import what you pick.
 *
 * Deliberately a separate screen rather than another section of the import
 * modal. Searching is a browsing activity -- type, read, reconsider, type again
 * -- and results need room; the modal is for when you already know the link.
 *
 * Importing reuses POST /recipes/import/url unchanged, so a recipe found here
 * arrives by exactly the same path as a pasted link, with the same schema.org
 * extraction and the same fallbacks. Nothing about discovery gets its own
 * import quirks.
 */
export default function DiscoverRecipes() {
  const navigate = useNavigate();
  const sources = useApi('/recipes/sources/list');
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState([]);
  const [fromIngredients, setFromIngredients] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  // Searching is explicit rather than debounced-as-you-type: each search is a
  // metered API call, and spending one per keystroke would burn a month's
  // allowance in an afternoon.
  const search = (event) => {
    event.preventDefault();
    const text = query.trim();
    // Commas stripped before joining: the names travel as one comma-separated
    // parameter, and "Tomatoes, Diced" as one pick must not arrive as two.
    const names = picked.map((item) => item.name.replace(/,/g, ' ').trim());
    if (!text && names.length === 0) return;

    // Picking ingredients changes the question from "find this dish" to "find
    // something I can cook", which is a different route -- and any text then
    // narrows that rather than replacing it. The label is kept because an
    // ingredient search has no query string of its own to quote back.
    setSubmitted(names.length > 0
      // 8: every result on this route costs a page fetch and a pantry check,
      // so the list is shorter than a plain search's on purpose.
      ? {
        path: `/recipes/sources/makeable${qs({ ingredients: names.join(','), q: text, limit: 8 })}`,
        label: text || names.join(', '),
        byIngredients: true,
      }
      // 10, not more: the Google provider is capped there by the API itself, so
      // a larger number asked for results that could never arrive.
      : { path: `/recipes/sources/search${qs({ q: text, limit: 10 })}`, label: text, byIngredients: false });
  };

  const results = useApi(submitted?.path || null, { skip: !submitted });

  // useApi keeps the previous path's data when the path changes, so a second
  // search briefly renders the first one's results underneath the new spinner,
  // and a failed search renders the last successful results beneath its error.
  // Nothing is shown unless it belongs to the request that is currently
  // settled.
  const shown = results.loading || results.error ? null : results.data;

  const setupNeeded = results.error?.code === 'RECIPE_SEARCH_NOT_CONFIGURED';
  const providerDown = results.error?.code === 'RECIPE_SEARCH_UNAVAILABLE'
    || results.error?.code === 'RECIPE_SEARCH_RATE_LIMITED';

  return (
    <div className="stack">
      {fromIngredients ? (
        <div className="card stack">
          <span className="small muted">Start from ingredients</span>
          <OnHandPicker selected={picked} onChange={setPicked} />
        </div>
      ) : (
        <div className="card stack">
          <button type="button" className="full" onClick={() => setFromIngredients(true)}>
            Start from ingredients
          </button>
        </div>
      )}

      <form className="row" onSubmit={search}>
        <input
          type="search"
          className="grow"
          placeholder="Search cooking sites…"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          aria-label="Search cooking sites"
        />
        <button type="submit" className="primary" disabled={!query.trim() && picked.length === 0}>
          Search
        </button>
      </form>

      {sources.data?.sources?.length > 0 && (
        <p className="tiny muted" style={{ margin: 0 }}>
          Searching {sources.data.sources.filter((s) => s.enabled).map((s) => s.name).join(', ')}.{' '}
          <Link to="/settings">Manage sources</Link>
        </p>
      )}

      {results.loading && <Loading rows={4} />}

      {setupNeeded || providerDown ? (
        // Neither is worth a retry button. One is a setting and one is a quota,
        // and the provider's own message says which -- so it is shown rather
        // than replaced with "request failed".
        <div className="alert warn">
          <strong>
            {setupNeeded ? 'Recipe search is not set up.' : 'Recipe search is unavailable.'}
          </strong>
          <p style={{ margin: '6px 0 0' }}>{results.error.message}</p>
          <p style={{ margin: '6px 0 0' }}>
            You can still add a recipe by pasting its link on the{' '}
            <Link to="/recipes">Recipes</Link> screen.
          </p>
        </div>
      ) : (
        <ErrorNote error={results.error} onRetry={results.reload} />
      )}

      {shown?.results?.length === 0 && (
        <Empty title={`Nothing found for “${shown.query || submitted.label}”`}>
          {submitted.byIngredients
            ? 'Try one fewer ingredient, or a pairing that turns up in recipes more often.'
            : 'Try fewer words, or a dish name rather than a list of ingredients.'}
        </Empty>
      )}

      <div className="stack">
        {/* An ingredient search puts a roundup's children in the roundup's own
            place in line, so a hit that came out of a list arrives here rather
            than inside one. One level is the whole rule either way. */}
        {(shown?.results || []).map((hit) => (
          <SearchHit
            key={hit.url}
            hit={hit}
            onImported={(recipe) => navigate(`/recipes/${recipe.id}`)}
            canExpand={!hit.from_collection}
          />
        ))}
      </div>

      {!submitted && !results.loading && (
        <Empty title="Find a recipe">
          Searches only the sites in your source list, and adds what you pick straight
          to your recipes.
        </Empty>
      )}
    </div>
  );
}
